<template>
  <div class="bb-logs">
    <transition name="fade" appear>
      <LoaderFly v-if="loaderRun"/>
    </transition>
    <h2 class="bb-page-title">Logs</h2>
    <div class="bb-logs-header">
      <div class="bb-logs-header-search">
        <input class="bb-logs-header-search__input bb-input bb-input_search"
               type="text"
               @input="inputSearch"
               placeholder="Search">
        <i class="icon-search bb-logs-header-search__icon"></i>
      </div>
      <FilterByDate @setDate="setFilterDate"
                    position="left"
                    all-name="All"
                    :border="true"/>
    </div>
    <div class="bb-logs-table">
      <Table :options="tableOptions" :totalPages="logsFullInfo.totalPages" :noDataText="noData"/>
      <Pagination :data="logsFullInfo" :getQuery="getLogs" :limit="defLimit" class="bb-pagination"/>
    </div>
  </div>
</template>

<script>
import {logsTable, logsTableColumns} from '@/constants/TablesOptions/logsTable'
import {
  LOG_ACTION_DELETE,
  LOG_ACTIONS,
  LOG_TYPE_PROJECT,
  LOG_TYPES,
  LOG_ACTION_DECLINED,
  LOG_TYPE_USER,
  LOG_TYPE_REQUEST,
  COMPANY
} from '@/constants/logTypes'
import Table from '@/components/Main/Table/Table'
import Pagination from '@/components/Main/Pagination/Pagination'
import LoaderFly from '@/components/Main/Loaders/LoaderFly'
import LogsApi from '@/api/Logs/api'
import {debounce, find, findIndex, get, isEmpty, pickBy, set, capitalize} from 'lodash'
import FilterByDate from '@/components/Main/FilterByDate/FilterByDate'
import convertNumber from '@/mixin/convertNumber'

export default {
  name: 'Logs',
  components: {
    LoaderFly,
    Pagination,
    Table,
    FilterByDate
  },
  mixins: [convertNumber],
  data () {
    return {
      loaderRun: false,
      logsFullInfo: {},
      defLimit: 30,
      tableOptions: logsTable,
      noDataText: 'no log entries',
      noData: false,
      filterByDate: '',
      searchValue: '',
      filters: {
        transactionAction: {},
        transactionType: {}
      },
      logTypes: LOG_TYPES,
      logActions: LOG_ACTIONS
    }
  },
  computed: {
    logsColumnsData: {
      get () {
        return this.$store.getters.logsColumnsData
      },
      set (data) {
        this.$store.dispatch('setLogsColumnsData', data)
      }
    },
    logsRowsData: {
      get () {
        return this.$store.getters.logsRowsData
      },
      set (data) {
        this.$store.dispatch('setLogsRowsData', data)
      }
    },
    mainScroll () {
      return this.$store.getters.mainScroll
    },
    getWithSearch () {
      return debounce(this.getLogs, 250)
    }
  },
  methods: {
    inputSearch (e) {
      const val = e.target.value
      this.searchValue = val.toLowerCase().trim()
      this.getWithSearch({})
    },
    setFilterDate (range) {
      this.filterByDate = range
      this.getLogs({})
    },
    setFilter (data, item, property) {
      set(this.filters, [property, item.id], data)
      this.getLogs({})
    },
    getFilters (propFilter) {
      const filterByProp = get(this.filters, propFilter, {})
      if (isEmpty(filterByProp)) return []
      const checkedValues = pickBy(filterByProp, item => item)
      return Object.keys(checkedValues)
    },
    getLogs ({page, limit = this.defLimit}) {
      this.loaderRun = true
      this.scrollPageTop()
      LogsApi.getLogs({
        page,
        limit,
        search: this.searchValue,
        filterByAction: this.getFilters('transactionAction'),
        filterByType: this.getFilters('transactionType'),
        filterByDate: this.filterByDate
      })
        .then(async resp => {
          this.logsRowsData = await this.getFullInfo(resp.data.clientlogs)
          this.loaderRun = false
          this.noData = resp.totalDocs ? false : this.noDataText
          this.logsFullInfo = resp.data
        })
        .catch(e => {
          this.loaderRun = false
          this.noData = this.noDataText
          console.error(e)
        })
    },
    async getFullInfo (data) {
      const updateItems = data.map(log => {
        const type = find(this.logTypes, {id: log.transactionType}) || {}
        log.transactionTypeId = log.transactionType
        log.transactionType = get(type, 'title', '')
        const action = find(this.logActions, {id: log.transactionAction}) || {}
        log.transactionAction = get(type, ['actions', action.key], action.title)
        const deletedProject = type.id === LOG_TYPE_PROJECT.id && +action.id === LOG_ACTION_DELETE.id
        const declinedRequest = type.id === LOG_TYPE_REQUEST.id && +action.id === LOG_ACTION_DECLINED.id
        const isUserType = type.id === LOG_TYPE_USER.id
        if (isUserType) {
          const username = get(log.user, 'username', '')
          log.client = {clientNickName: username, ...log.user}
        }
        if (declinedRequest || deletedProject) {
          log.$info = log.comments.map(item => capitalize(item)).join('. ')
        }
        if (log.reference && !isNaN(+log.reference)) log.reference = this.convertNumber(log.reference)
        if (log.details && !isNaN(+log.details) && COMPANY.id !== log.transactionTypeId) {
          log.details = this.convertNumber(log.details)
        }
        return log
      })
      return updateItems
    },
    deleteLog (id) {
      const index = findIndex(this.logsRowsData, {id: id})
      const deletedLog = index !== -1 ? this.logsRowsData.splice(index, 1) : false
      LogsApi.deleteLog(id)
        .then(() => {
        })
        .catch(e => {
          if (deletedLog) this.logsRowsData.splice(index, 0, ...deletedLog)
          console.error(e)
        })
    },
    scrollPageTop () {
      const scroll = get(this.mainScroll, 'target', '')
      if (!scroll) return
      scroll.scrollTop = 70
    }
  },
  beforeMount () {
    this.logsColumnsData = logsTableColumns.map(item => {
      if (item.list) {
        item.actions.filter = this.setFilter
        item.list.forEach((el) => {
          el.checked = false
        })
      }
      if (item.actions) {
        item.actions.delete = this.deleteLog
      }
      return item
    })
  }
}
</script>

<style lang="scss" scoped>
@import "../../../sass/variables";
@import "../../../sass/mixins";

.bb-logs {
  @include global-font;

  &-header {
    margin-top: 14px;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;

    &-search {
      position: relative;
      display: flex;
      align-items: center;
      flex-basis: 578px;

      &__input {
        width: 100%;
        height: 42px;
        border-radius: 6px;
        padding-left: 40px;
      }

      &__icon {
        position: absolute;
        left: 13px;
        font-size: 15px;
        color: $color-silver;
      }
    }
  }

  &-table {
    display: grid;
    position: relative;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/Settings/Logs/Logs.vue